/**
 * A project's protocol section, in a drawer `?health=<projectId>` opens from the Projects page
 * (and from problem links): its open problems, how each state file reports, where its pipeline
 * comes from, its declared actions and their last runs, and its deploy targets.
 */
import { Activity, Check, Play, ScrollText } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { pipelineKey } from '@orgus/protocol';
import type { DeployTarget, Id, Problem, ProjectActionDecl, StateFileStatus } from '@orgus/protocol';
import {
  Button,
  Drawer,
  ErrorState,
  IconButton,
  ListRow,
  ProjectMark,
  RelativeTime,
  RowCell,
  RowList,
  RowStatus,
  RowTitle,
  Segmented,
  SkeletonRows,
  Tooltip,
  cn,
  formatDuration,
  formatInt,
  formatRelative,
  plural,
} from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useProjectProtocol } from './api.ts';
import { ProblemRow } from './Problems.tsx';
import { FILE_NAME, FILE_ORDER, deployStatus, fileRows, holdById, menuActions, problemStatus, projectRuns, reportingStatus, reportsForProject, roleBindings, runStatus, sortProblems } from './model.ts';
import type { ProjectReport, StatusView } from './model.ts';
import { openDeclaredAction } from './store.ts';
import { DeployLogDialog, deployLogRef } from './RunLog.tsx';
import { DetailSection, FactGrid } from '../detail/FactGrid.tsx';
import { shortRemote } from '../projects/model.ts';

export function useHealthParam(): [string | null, (id: Id | null) => void] {
  const [params, setParams] = useSearchParams();
  const id = params.get('health');
  const set = useCallback(
    (next: Id | null) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next) p.set('health', next);
          else p.delete('health');
          return p;
        },
        { replace: !next, preventScrollReset: true },
      ),
    [setParams],
  );
  return [id, set];
}

const ROLE_LABEL = { 'pipeline.pause': 'Board pause', 'pipeline.resume': 'Board resume', 'pipeline.restart': 'Board restart' } as const;

function manifestStatus(r: ProjectReport): StatusView {
  if (r.manifestError) return { tone: 'danger', icon: 'failed', label: 'Invalid' };
  if (r.manifest) return { tone: 'neutral', icon: 'done', label: 'Committed' };
  return { tone: 'neutral', icon: 'idle', label: 'Not found' };
}

/** What one state file cell says on hover: why, when it was last written, how often it should be. */
function fileDetail(f: StateFileStatus): string {
  if (f.state === 'absent') return 'Not written, not declared';
  return [f.reason, f.updatedAt != null ? `Written ${formatRelative(f.updatedAt)}` : null, !f.reason && f.intervalS ? `every ${formatDuration(f.intervalS * 1000)}` : null].filter(Boolean).join(', ');
}

/** A status cell with its detail on hover. */
function StatusCell({ st, detail }: { st: StatusView; detail: string }) {
  return (
    <Tooltip content={detail}>
      <span tabIndex={0} className="inline-flex min-w-0 rounded-control">
        <RowStatus {...st} />
      </span>
    </Tooltip>
  );
}

/**
 * How each state file reports, as one small grid: a row per file, a column per machine. The
 * detail of each cell (last write, interval, why) is on hover.
 */
function ReportingGrid({ reports, machineName }: { reports: ProjectReport[]; machineName: (id: Id) => string }) {
  const many = reports.length > 1;
  const rows: { name: string; cells: { key: string; st: StatusView; detail: string }[] }[] = [
    {
      name: 'orgus.yml',
      cells: reports.map((r) => ({ key: r.machineId, st: manifestStatus(r), detail: r.manifestError ?? (r.manifest ? 'At HEAD of the main checkout' : 'Defaults apply') })),
    },
    ...FILE_ORDER.map((kind) => ({
      name: FILE_NAME[kind],
      cells: reports.map((r) => {
        const f = fileRows(r).find((x) => x.kind === kind)!;
        return { key: r.machineId, st: reportingStatus(f.state), detail: fileDetail(f) };
      }),
    })),
  ];
  return (
    <div role="table" aria-label="State files" className="flex flex-col rounded-card border border-border-subtle p-1">
      {many && (
        <div role="row" className="flex h-control-sm items-center gap-3 px-3 text-caption text-fg-subtle">
          <span role="columnheader" className="min-w-0 flex-1">
            File
          </span>
          {reports.map((r) => (
            <span key={r.machineId} role="columnheader" className="w-28 shrink-0 truncate">
              {machineName(r.machineId)}
            </span>
          ))}
        </div>
      )}
      {rows.map((row) => (
        <div key={row.name} role="row" className="flex h-row items-center gap-3 rounded-control px-3">
          <span role="rowheader" className="min-w-0 flex-1 truncate font-mono text-caption text-fg">
            {row.name}
          </span>
          {row.cells.map((c) => (
            <span key={c.key} role="cell" className={cn('flex min-w-0 shrink-0', many ? 'w-28' : 'justify-end')}>
              <StatusCell st={c.st} detail={c.detail} />
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function ActionRow({ a, report }: { a: ProjectActionDecl; report: ProjectReport }) {
  return (
    <ListRow
      multiline
      lead={<Play className="size-4 text-fg-subtle" aria-hidden />}
      actions={
        <Button size="sm" variant="secondary" icon={<Play />} aria-label={`${a.label}…`} onClick={() => openDeclaredAction({ projectId: report.projectId, machineId: report.machineId, actionId: a.id })}>
          Run…
        </Button>
      }
      trail={
        (a.role || a.heavy) && (
          <RowCell width="w-28" hideBelow="sm" className="justify-end">
            {a.role ? ROLE_LABEL[a.role] : 'Yields to landings'}
          </RowCell>
        )
      }
    >
      <RowTitle title={a.label} meta={a.does} />
    </ListRow>
  );
}

function DeployRow({ t, onLog }: { t: DeployTarget; onLog: () => void }) {
  const st = deployStatus(t);
  return (
    <ListRow
      multiline
      lead={<RowStatus {...st} hideLabel />}
      actions={t.last?.logRef ? <IconButton label={`Show the ${t.label} deploy log`} icon={<ScrollText />} size="xs" onClick={onLog} /> : undefined}
      trail={
        <RowCell width="w-16" numeric>
          {t.last?.at != null ? <RelativeTime ts={t.last.at} /> : null}
        </RowCell>
      }
    >
      <RowTitle
        title={t.label}
        meta={
          <span className="flex min-w-0 gap-2">
            <span className="min-w-0 truncate" data-meta>
              {st.label}
            </span>
            {t.live && (
              <span className="shrink-0 font-mono" data-meta>
                {t.live.version}
                {t.behind && t.mainVersion ? ` to ${t.mainVersion}` : ''}
              </span>
            )}
          </span>
        }
      />
    </ListRow>
  );
}

/** The actions a project declares, once: with several machines, a switch picks where they run. */
function Actions({ reports, machineName }: { reports: ProjectReport[]; machineName: (id: Id) => string }) {
  const [on, setOn] = useState(reports[0]!.machineId);
  const r = reports.find((x) => x.machineId === on) ?? reports[0]!;
  const list = [...menuActions(r), ...Object.values(roleBindings(r))];
  return (
    <DetailSection
      title="Actions"
      aside={formatInt(list.length)}
      collapsible
      actions={
        reports.length > 1 ? (
          <Segmented size="sm" label="Run on" value={r.machineId} onChange={setOn} options={reports.map((x) => ({ value: x.machineId, label: machineName(x.machineId) }))} />
        ) : undefined
      }
    >
      <RowList label="Declared actions" className="rounded-card border border-border-subtle">
        {list.map((a) => (
          <ActionRow key={a.id} a={a} report={r} />
        ))}
      </RowList>
    </DetailSection>
  );
}

/** The state files in one fact: all reporting, or how many are not. */
function reportingFact(reports: ProjectReport[], quiet = false): ReactNode {
  if (!reports.length) return null;
  const files = reports.flatMap((r) => r.files.filter((f) => f.state !== 'absent'));
  const off = files.filter((f) => f.state === 'not-reporting').length;
  if (reports.some((r) => r.manifestError)) return quiet ? 'Invalid orgus.yml' : <RowStatus tone="danger" icon="failed" label="Invalid orgus.yml" />;
  const label = `${formatInt(off)} of ${formatInt(files.length)} not reporting`;
  if (off) return quiet ? label : <RowStatus tone="attention" icon="warning" label={label} />;
  return files.length ? `All ${formatInt(files.length)} files` : 'No state files';
}

const PIPELINE_SHORT: Record<ProjectReport['pipelineSource'], string> = { protocol: 'pipeline.json', fallback: 'Temporary adapter', none: 'None' };

export function ProjectHealthBody({ projectId }: { projectId: Id }) {
  const { state, phase, error, retry } = useProjectProtocol();
  const machines = useOrgus((s) => s.machines);
  const reports = reportsForProject(state, projectId);
  const problems = useMemo(() => sortProblems((state?.problems ?? []).filter((p) => p.projectId === projectId)), [state, projectId]);
  const runs = projectRuns(state, projectId);
  const [deployLog, setDeployLog] = useState<{ id: string; label: string; version: string | null; logRef: string } | null>(null);

  if (phase === 'loading') return <SkeletonRows rows={6} />;
  if (phase === 'error') return <ErrorState variant="inline" error={error} onRetry={retry} />;

  const machineName = (id: Id) => machines[id]?.name ?? 'Unknown machine';
  const withActions = reports.filter((r) => (r.manifest?.actions.length ?? 0) > 0);
  const deploys = reports.flatMap((r) => r.deploys.map((t) => ({ r, t })));
  const actionName = (runActionId: string, rid: Id) => reports.find((r) => r.machineId === rid)?.manifest?.actions.find((a) => a.id === runActionId)?.label ?? runActionId;

  return (
    <div className="flex flex-col gap-6">
      <DetailSection title="Problems" aside={problems.length ? formatInt(problems.length) : undefined}>
        {problems.length ? (
          <RowList label="Problems" className="rounded-card border border-border-subtle">
            {problems.map((p) => (
              <ProblemRow key={p.key} problem={p} showSource={false} />
            ))}
          </RowList>
        ) : (
          <p className="flex h-row items-center gap-2 text-ui text-fg-subtle">
            <Check className="size-4" aria-hidden />
            No open problems
          </p>
        )}
      </DetailSection>

      {deploys.length > 0 && (
        <DetailSection title="Deploys" aside={formatInt(deploys.length)}>
          <RowList label="Deploy targets" className="rounded-card border border-border-subtle">
            {deploys.map(({ r, t }) => (
              <DeployRow
                key={`${r.machineId}:${t.id}`}
                t={t}
                onLog={() => setDeployLog({ id: t.id, label: t.label, version: t.last?.version ?? null, logRef: t.last?.logRef ?? deployLogRef(t.id) })}
              />
            ))}
          </RowList>
        </DetailSection>
      )}

      {runs.length > 0 && (
        <DetailSection title="Recent runs">
          <RowList label="Recent runs" className="rounded-card border border-border-subtle">
            {runs.map((run) => {
              const st = runStatus(run, holdById(state, run.yieldId));
              return (
                <ListRow
                  key={run.id}
                  multiline
                  lead={<RowStatus {...st} hideLabel focusable={false} />}
                  onOpen={() => openDeclaredAction({ projectId: run.projectId, machineId: run.machineId, actionId: run.actionId, runId: run.id })}
                  trail={
                    <RowCell width="w-16" numeric>
                      {run.startedAt != null ? <RelativeTime ts={run.startedAt} /> : null}
                    </RowCell>
                  }
                >
                  <RowTitle title={actionName(run.actionId, run.machineId)} meta={<span data-meta>{st.label}</span>} />
                </ListRow>
              );
            })}
          </RowList>
        </DetailSection>
      )}

      {withActions.length > 0 && <Actions reports={withActions} machineName={machineName} />}

      <DetailSection title="Reporting" collapsible aside={reportingFact(reports, true) ?? 'Not reported yet'}>
        {reports.length ? <ReportingGrid reports={reports} machineName={machineName} /> : <p className="text-ui text-fg-subtle">No machine has reported this project yet</p>}
      </DetailSection>
      <DeployLogDialog target={deployLog} onClose={() => setDeployLog(null)} />
    </div>
  );
}

/** The project's worst open problem as its header status, or a quiet check when there is none. */
function healthStatus(problems: readonly Problem[]): StatusView {
  if (!problems.length) return { tone: 'neutral', icon: 'done', label: 'No open problems' };
  return { ...problemStatus(problems[0]!.severity), label: plural(problems.length, 'open problem') };
}

/** The drawer, for `?health=<projectId>`. Mounted once in the shell. */
export function ProjectHealthDrawerHost() {
  const [id, setId] = useHealthParam();
  const navigate = useNavigate();
  const project = useOrgus((s) => (id ? s.projects[id] : undefined));
  const machines = useOrgus((s) => s.machines);
  const pipelines = useOrgus((s) => s.pipelines);
  const { state } = useProjectProtocol();
  if (!id) return null;
  const reports = reportsForProject(state, id);
  const problems = sortProblems((state?.problems ?? []).filter((p) => p.projectId === id));
  const pipeline = Object.values(pipelines).find((p) => p.projectId === id);
  const names = [...new Set(reports.map((r) => r.machineId))].map((m) => machines[m]?.name ?? 'Unknown machine');
  const remote = shortRemote(project?.remoteUrl ?? null);
  const source = reports.find((r) => r.pipelineSource === 'protocol')?.pipelineSource ?? reports[0]?.pipelineSource;
  return (
    <Drawer
      open
      onOpenChange={(v) => !v && setId(null)}
      width={640}
      layout="rows"
      bodyClassName="px-6 py-4"
      eyebrow={
        <>
          <span className="text-caption text-fg-subtle">Project health</span>
          {state && <RowStatus {...healthStatus(problems)} />}
        </>
      }
      title={
        <span className="flex min-w-0 items-center gap-2">
          {project && <ProjectMark projectId={project.id} name={project.name} size="sm" />}
          <span className="min-w-0 truncate">{project?.name ?? 'Unknown project'}</span>
        </span>
      }
      description={
        <FactGrid
          label="Project facts"
          lines={[
            [
              { label: 'Repository', value: remote ?? 'Local only', title: project?.remoteUrl ?? undefined },
              { label: names.length > 1 ? 'Machines' : 'Machine', value: names.length ? (names.length > 2 ? plural(names.length, 'machine') : names.join(', ')) : 'None yet', title: names.join(', ') },
            ],
            [
              { label: 'Pipeline', value: source ? PIPELINE_SHORT[source] : pipeline ? pipeline.title : 'None', mono: source === 'protocol' },
              { label: 'Reporting', value: reportingFact(reports) ?? 'Not yet' },
            ],
          ]}
        />
      }
      footer={
        pipeline ? (
          <Button
            variant="primary"
            icon={<Activity />}
            className="ml-auto"
            onClick={() => {
              setId(null);
              navigate(`/pipelines?pipeline=${encodeURIComponent(pipelineKey(pipeline))}`);
            }}
          >
            Open pipeline
          </Button>
        ) : undefined
      }
    >
      <ProjectHealthBody projectId={id} />
    </Drawer>
  );
}
